/**
 * Whether this tab was in the headset when its page last went. A reload, a crash or a deploy
 * ends the session, and the player was left on the flat page with the small VR toggle as the only
 * way back (x-quest-vr#14). The mark is kept for the tab alone (sessionStorage) and cleared when
 * the player leaves VR, but not when the page is what goes: the browser ends its XR sessions
 * after `pagehide`.
 */
const KEY = 'archon3d.inVr';

function tabStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Whether the tab was in VR when its page went, so Home offers the way back. */
export function wasInVr(storage: Storage | null = tabStorage()): boolean {
  try {
    return storage?.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Keeps the mark as the headset takes over and lets go; returns the stop. */
export function watchVrReturn(
  shell: { getSnapshot(): { inXr: boolean }; subscribe(listener: () => void): () => void },
  storage: Storage | null = tabStorage(),
  page: EventTarget | null = typeof window === 'undefined' ? null : window,
): () => void {
  let leaving = false;
  let inXr = shell.getSnapshot().inXr;
  const onPageHide = () => {
    leaving = true;
  };
  page?.addEventListener('pagehide', onPageHide);
  const stop = shell.subscribe(() => {
    const now = shell.getSnapshot().inXr;
    if (now === inXr) return;
    inXr = now;
    if (!now && leaving) return;
    try {
      if (now) storage?.setItem(KEY, '1');
      else storage?.removeItem(KEY);
    } catch {
      // Storage refused (a private window): Home simply offers nothing after a reload.
    }
  });
  return () => {
    stop();
    page?.removeEventListener('pagehide', onPageHide);
  };
}
